<script setup>
import { ref, computed, onMounted } from 'vue'
import { api } from '../composables/useApi.js'
import ShareButtons from '../components/ShareButtons.vue'
import {
    formatSecondsToFullTime,
    formatTimestamp,
} from '../composables/useFormatting.js'

const origin = window.location.origin

const props = defineProps({
    pressId: {
        type: Number,
        required: true,
    },
    username: {
        type: String,
        required: false,
        default: null,
    },
})

const press = ref(null)
const loading = ref(true)

const pressUrl = computed(() => {
    if (props.username) {
        return `${origin}/user/${props.username}/press/${props.pressId}/`
    }
    return `${origin}/press/${props.pressId}/`
})

const shareText = computed(() => {
    if (!press.value) return ''
    const userText = props.username ? `${props.username}'s` : 'An anonymous'
    return `${userText} button press claimed ${formatSecondsToFullTime(press.value.elapsed_seconds)} and achieved the ${press.value.tier_label || press.value.tier} tier! #thebutton`
})

const userProfileUrl = computed(() => {
    if (props.username) {
        return `${origin}/user/${props.username}/`
    }
    return null
})

const loadData = () => {
    api.get(`/api/buttonpresses/${props.pressId}/`)
        .then((response) => {
            press.value = response.data
        })
        .catch((error) => {
            console.error('Error fetching press:', error)
        })
        .finally(() => {
            loading.value = false
        })
}

onMounted(() => {
    loadData()
})
</script>

<template>
    <div class="c-press-detail">
        <div class="c-press-detail__container">
            <div v-if="loading" class="c-press-detail__loading">
                Loading Press Details...
            </div>

            <div v-else-if="!press" class="c-press-detail__error glass">
                Press not found.
            </div>

            <div v-else class="c-press-detail__content">
                <!-- Breadcrumb navigation -->
                <nav class="c-breadcrumb">
                    <a href="/" class="c-breadcrumb__link">Home</a>
                    <span class="c-breadcrumb__separator">/</span>
                    <a
                        v-if="userProfileUrl"
                        :href="userProfileUrl"
                        class="c-breadcrumb__link"
                    >
                        {{ username }}
                    </a>
                    <span v-if="userProfileUrl" class="c-breadcrumb__separator"
                        >/</span
                    >
                    <span class="c-breadcrumb__current">
                        Press #{{ pressId }}
                    </span>
                </nav>

                <!-- Header -->
                <div class="c-press-detail__header">
                    <h1 class="c-press-detail__title">
                        Button Press #{{ press.id }}
                    </h1>
                    <p class="c-press-detail__subtitle">
                        <span v-if="username">by {{ username }}</span>
                        <span v-else>by Anonymous</span>
                    </p>
                </div>

                <!-- Main press card -->
                <div class="c-press-detail__card">
                    <div class="c-press-detail__stats-grid">
                        <div class="c-press-detail__stat">
                            <span class="c-press-detail__stat-label"
                                >Time Claimed</span
                            >
                            <span class="c-press-detail__stat-value">
                                {{
                                    formatSecondsToFullTime(
                                        press.elapsed_seconds
                                    )
                                }}
                            </span>
                        </div>

                        <div class="c-press-detail__stat">
                            <span class="c-press-detail__stat-label"
                                >Pressed At</span
                            >
                            <span
                                class="c-press-detail__stat-value c-press-detail__stat-value--small"
                            >
                                {{ formatTimestamp(press.timestamp) }}
                            </span>
                        </div>

                        <div
                            class="c-press-detail__stat c-press-detail__tier-stat"
                        >
                            <span class="c-press-detail__stat-label"
                                >Tier Achieved</span
                            >
                            <span
                                class="tier-badge c-press-detail__tier-badge"
                                :class="`tier-${press.tier.toLowerCase()}`"
                            >
                                {{ press.tier_label || press.tier }}
                            </span>
                        </div>
                    </div>

                    <div class="c-press-detail__share">
                        <ShareButtons
                            :url="pressUrl"
                            :text="shareText"
                            :show-label="true"
                        />
                    </div>
                </div>

                <!-- Back to profile button -->
                <div v-if="userProfileUrl" class="c-press-detail__actions">
                    <a
                        :href="userProfileUrl"
                        class="c-press-detail__back-button"
                    >
                        View All Presses by {{ username }}
                    </a>
                </div>
            </div>
        </div>
    </div>
</template>
